import { format } from "date-fns";
import { getScrutinyDefectMemo } from "./service";

export const isEmailMandatory = (profileType) => {
  return ['mediator', 'case_manager', 'sub_admin_tiac_rv', 'arbitrator_rv'].includes(profileType);
}

export const isBacTempEmail = (email) => {
  return typeof email === "string" && email.startsWith("bac_temp_email");
}

export const displayEmail = (email) => {
  return !email || isBacTempEmail(email) ? "-" : email;
}

// Extra emails a party receives case notifications on. They are stored per
// dispute + profile + party type (DisputePartyEmail), not on the profile, so
// the profile API never returns them. The case details API puts them on each
// party entry (beside `profile` and `user`); tolerate plain strings or
// { email } rows.
export const getAdditionalEmails = (partyData) => {
  const list = partyData?.additional_emails;
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => (typeof item === "string" ? item : item?.email))
    .filter(Boolean);
};

// Organisation details of a claimant / respondent. Registration stores the
// "Is ... an Organisation?" answer in profile.is_validated and the
// representative's name in profile.bar_enrollment (for counsel that field is
// the real bar number, so only read it for parties).
export const getPartyOrganisation = (profile) => {
  const isOrganisation =
    profile?.is_validated === true || String(profile?.is_validated).toLowerCase() === "true";
  const raw = String(profile?.bar_enrollment ?? "").trim();
  const representative =
    isOrganisation && !["", "null", "none", "undefined"].includes(raw.toLowerCase()) ? raw : "";
  return { isOrganisation, representative };
};

export const profileMatchesSearch = (profile, search) => {
  const query = (search || "").trim().toLowerCase();
  if (!query) return true;

  const email = profile?.user?.email;
  const fields = [
    `${profile?.user?.first_name || ""} ${profile?.user?.last_name || ""}`,
    isBacTempEmail(email) ? "" : email,
    profile?.phone_number,
    profile?.bar_enrollment,
  ];

  return fields.some(
    (field) => field && String(field).toLowerCase().includes(query)
  );
};

// A case counts as reopened once it has a reopen date (or the backend reports a
// "reopened" status) and has not been closed again since.
export const isCaseReopened = (caseData) => {
  if (!caseData) return false;
  const status = String(caseData.status || "").toLowerCase();
  if (status === "closed") return false;
  return caseData?.reopened || Boolean(caseData.reopen_date);
};

// Status label for display: "Reopened" for a reopened case, otherwise the raw
// status with its first letter capitalised.
export const getCaseStatusLabel = (caseData) => {
  if (isCaseReopened(caseData)) return "Reopened";
  const status = String(caseData?.status || "").trim();
  // A closed case is shown as "Disposal" throughout the BAC portal.
  if (status.toLowerCase() === "closed") return "Disposal";
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : "";
};

// "AC No. 514/2026" -> { prefix: "AC", number: "514", year: "2026" }, the
// shape SearchCaseForm takes as `initialValues`. Null if it doesn't match.
export const parseCaseNumber = (caseNumber) => {
  const match = String(caseNumber || "")
    .trim()
    .match(/^([A-Za-z]+)\s+No\.?\s*(\d+)\s*\/\s*(\d{4})$/);
  if (!match) return null;
  return { prefix: match[1].toUpperCase(), number: match[2], year: match[3] };
};

// Display name of an order draft's format, from the list served by
// /bac/arbitrator/order-drafts/formats/. Drafts saved before formats existed
// have none and are ACCB order sheets.
export const getDraftFormatLabel = (formats, formatKey) => {
  const key = formatKey || "order_sheet";
  const match = (formats || []).find((f) => f.key === key);
  if (match) return match.label;
  if (key === "order_sheet") return "ACCB Order Sheet";
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// Formats a case can have several drafts of, each named in its own sequence:
// PW1, PW2, … and RW1, RW2, …. Every other format allows one draft per case.
const SEQUENCED_DRAFT_PREFIXES = {
  deposition_pw2: "PW",
  deposition_rw2: "RW",
};

export const allowsMultipleDrafts = (formatKey) =>
  Boolean(SEQUENCED_DRAFT_PREFIXES[formatKey]);

// The name for a new draft of `formatKey`: one past the highest number already
// used, so a gap is never refilled and a name never repeats. PW/RW count only
// drafts of their own format; every other format shares "Draft 1", "Draft 2", ….
export const nextDraftTitle = (drafts, formatKey) => {
  const prefix = SEQUENCED_DRAFT_PREFIXES[formatKey];
  const pattern = prefix
    ? new RegExp("^\\s*" + prefix + "\\s*(\\d+)\\s*$", "i")
    : /^\s*Draft\s+(\d+)\s*$/i;
  let max = 0;
  (drafts || []).forEach((d) => {
    if (prefix && (d.format || "order_sheet") !== formatKey) return;
    const m = pattern.exec(d.title || "");
    if (m) max = Math.max(max, parseInt(m[1], 10));
  });
  return prefix ? `${prefix}${max + 1}` : `Draft ${max + 1}`;
};

// Amount inputs (fees, claim amounts, payments) take up to 15 whole-rupee
// digits and 2 decimals. Use in onChange:
// ignore the keystroke when this returns false.
export const isAllowedAmountInput = (value) =>
  /^\d{0,15}(\.\d{0,2})?$/.test(String(value ?? ""));

// A hearing-room fee in rupees ("₹1,500"), or null when the backend sent
// none, so a room or slot without a fee shows nothing rather than "₹0".
export const formatFee = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    // "₹5,000", "₹1,500.50": paise only when there are some.
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const hasFee = (value) => formatFee(value) !== null;

// What booking `selectedSlotIds` in `room` costs, from /bac/get-open-rooms/:
// the room's full_day_fee when every slot of the room is picked (what "Book
// full day" does), otherwise the picked slots' applicable_fee added up.
// Returns { amount, fullDay, unpriced } (unpriced = picked slots with no fee,
// left out of the total), or null when none of the picks has a fee.
export const getBookingFee = (room, selectedSlotIds) => {
  const slots = (room && room.slots) || [];
  const chosen = slots.filter((slot) => selectedSlotIds.includes(slot.slot_id));
  if (!chosen.length) return null;
  if (chosen.length === slots.length && hasFee(room.full_day_fee)) {
    return { amount: Number(room.full_day_fee), fullDay: true, unpriced: 0 };
  }
  const priced = chosen.filter((slot) => hasFee(slot.applicable_fee));
  if (!priced.length) return null;
  return {
    amount: priced.reduce((sum, slot) => sum + Number(slot.applicable_fee), 0),
    fullDay: false,
    unpriced: chosen.length - priced.length,
  };
};

export const isValidPhone = (phone) => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone);
};

export const safeDateFormat = (raw, formatTo = 'dd/MM/yyyy') => {
  if (!raw) return '-';

  let d;

  if (raw instanceof Date) {
    d = raw;
  }
  else if (!isNaN(raw) && !isNaN(parseFloat(raw))) {
    d = new Date(Number(raw));
  }
  else if (typeof raw === 'string') {
    const trimmed = raw.trim();

    // 1. Support 12-hour or 24-hour time-only formats (e.g., "11:30:54", "11:30 PM", "11:30:54 AM")
    const timeOnlyRegex = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(?:\s*(AM|PM))?$/i;
    const timeMatch = trimmed.match(timeOnlyRegex);

    if (timeMatch) {
      let [_, hourStr, minuteStr, secondStr = '0', ampm] = timeMatch;
      let hour = Number(hourStr);
      const minute = Number(minuteStr);
      const second = Number(secondStr);

      // Convert 12-hour AM/PM to 24-hour for the Date object
      if (ampm) {
        const period = ampm.toUpperCase();
        if (period === 'PM' && hour < 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
      }

      d = new Date(); // Temporary base date to hold the time context
      d.setHours(hour, minute, second, 0);
    }
    // 2. Existing functionality: Catch DD-MM-YYYY or DD/MM/YYYY with optional time
    else {
      const dateTimeRegex = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(?:\s*(AM|PM))?)?$/i;
      const match = trimmed.match(dateTimeRegex);

      if (match) {
        let [_, day, month, year, hourStr = '0', minuteStr = '0', secondStr = '0', ampm] = match;
        let hour = Number(hourStr);

        if (ampm && hourStr !== '0') {
          const period = ampm.toUpperCase();
          if (period === 'PM' && hour < 12) hour += 12;
          if (period === 'AM' && hour === 12) hour = 0;
        }

        d = new Date(Number(year), Number(month) - 1, Number(day), hour, Number(minuteStr), Number(secondStr));
      } else {
        // Fallback for standard ISO / fallback strings
        d = new Date(trimmed);
      }
    }
  }
  else {
    d = new Date(raw);
  }

  return isNaN(d.getTime()) ? '-' : format(d, formatTo);
};

export const parseDateString = (str) => {
  if (!str) return null;
  if (str instanceof Date) return str;

  const trimmed = str.trim();
  const regex = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(?:\s*(AM|PM))?)?$/i;
  const match = trimmed.match(regex);

  if (match) {
    let [_, day, month, year, hourStr = '0', minuteStr = '0', secondStr = '0', ampm] = match;
    let hour = Number(hourStr);

    if (ampm && hourStr !== '0') {
      const period = ampm.toUpperCase();
      if (period === 'PM' && hour < 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
    }

    const d = new Date(Number(year), Number(month) - 1, Number(day), hour, Number(minuteStr), Number(secondStr));
    return isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
};


const profileOf = (groData) => groData?.profile || groData || {};

export const getProfileRegion = (groData) => {
  const p = profileOf(groData);
  return p.region || p.state || "";
};

export const getProfileCountry = (groData) => {
  const country = groData?.country;
  if (country) {
    return typeof country === "object" ? country.name || "" : String(country);
  }
  return profileOf(groData).nationality || "";
};

export const formatPartyName = (parties) => {
  if (!parties || parties?.length === 0) return "";

  if (typeof parties === "string") return parties;

  const firstParty = parties[0];
  let fullName = "";

  if (typeof firstParty === "string") {
    fullName = firstParty.trim();
  } else if (firstParty?.profile?.user) {
    const firstName = firstParty.profile.user.first_name || "";
    const lastName = firstParty.profile.user.last_name || "";
    fullName = `${firstName} ${lastName}`.trim();
  } else {
    return "";
  }

  if (parties.length > 2) {
    return `${fullName} and Others`;
  } else if (parties.length === 2) {
    return `${fullName} and Anr`;
  }

  return fullName;
};

export async function downloadDefectMemo(scrutinyId, onError) {
  try {
    const res = await getScrutinyDefectMemo(scrutinyId);
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `defect_memo_${scrutinyId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (err) {
    console.error("Error downloading defect memo:", err);
    if (onError) {
      onError("Could not download the defect memo. Please try again.");
    }
  }
}

const escapeHtml = (value) =>
	String(value === null || value === undefined ? "" : value)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");

export const buildHearingDatesHtml = (hearings) => {
	const rows = (hearings || []).filter((h) => h && h.listing_date);
	if (rows.length === 0) return "";
	const lines = rows
		.map((h) =>
			escapeHtml(
				h.label || `${h.stage || "Hearing"} — ${h.listing_date}`
			)
		)
		.join("<br />");
	const gap = "<p>&nbsp;</p>".repeat(3);
	return `${gap}<p><strong>Next Hearing Dates:</strong><br />${lines}</p>`;
};

// CMP cases carry a CMP number and Reference cases an MSEFC number, both
// stored in request_ticket_number (sometimes with stray quotes/backslashes,
// stripped here as SingleDispute does). Returns null when neither applies, so
// callers render nothing for Institutional cases or a missing number.
export const getCaseReferenceNumber = (caseData) => {
	const category = String(caseData?.category || caseData?.customer_type || "")
		.replace(/[\\"']/g, "")
		.trim()
		.toLowerCase();
	const value = String(caseData?.request_ticket_number || "")
		.replace(/[\\"]/g, "")
		.trim();
	if (!value) return null;
	if (category === "cmp") return { label: "CMP Number", value };
	if (["reference", "msefc", "msed"].includes(category)) {
		return { label: "MSEFC Number", value };
	}
	return null;
};
